import { Building2 } from 'lucide-react';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { Kbd } from 'v5/design-system/Kbd';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import { cn } from 'v5/platform/dom/cn';
import {
  FeatureFlagEntry,
  FeatureFlagValueConfig
} from 'v5/services/front/FeatureManagementService/types';
import { FeatureManagementActionHandler } from 'v5/services/front/FeatureManagementService/FeatureManagementService';
import {
  bulkServerScopesState,
  bulkSetServerScopes
} from './featureFlagCatalogBulkActions';
import {
  FeatureFlagBulkBar,
  FeatureFlagDetailSearch,
  FeatureFlagToggleListBox,
  FrontendOverrideControl,
  featureFlagBulkStatus
} from './FeatureFlagInspectorControls';

function matchesDetailSearch(label: string, query: string): boolean {
  if (!query) {
    return true;
  }
  return label.toLowerCase().includes(query);
}

export interface FeatureFlagInspectorProps {
  entry: FeatureFlagEntry | null;
  workspaceColumns: Array<{ id: number; name: string }>;
  detailSearch: string;
  onDetailSearchChange: (search: string) => void;
  onAction: FeatureManagementActionHandler;
}

export function FeatureFlagInspector({
  entry,
  workspaceColumns,
  detailSearch = '',
  onDetailSearchChange = () => undefined,
  onAction
}: FeatureFlagInspectorProps) {
  if (!entry) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-8 text-sm text-fg-muted">
        Select a flag to configure it.
      </div>
    );
  }

  const query = detailSearch.trim().toLowerCase();
  const hasServer = entry.config.legacy.supported;
  const hasFrontend = entry.config.frontend.supported;

  const includeLegacy =
    hasServer &&
    entry.config.legacy.supported &&
    matchesDetailSearch('Legacy', query);
  const filteredWorkspaces = workspaceColumns.filter(column =>
    matchesDetailSearch(column.name, query)
  );
  const includeFrontend =
    hasFrontend &&
    (matchesDetailSearch('This browser', query) ||
      matchesDetailSearch('Local override', query) ||
      matchesDetailSearch('frontend', query));

  const filteredWorkspaceIds = filteredWorkspaces.map(column => column.id);
  const bulkState = bulkServerScopesState(entry, filteredWorkspaceIds, {
    includeLegacy
  });

  const setAction = (
    source: 'legacy' | 'frontend' | { workspaceId: number },
    value: FeatureFlagValueConfig<false>['current']
  ) => {
    onAction({
      type: 'setFeatureFlag',
      source,
      featureId: entry.featureId,
      value
    });
  };

  const serverItems = [
    ...(includeLegacy
      ? [
          {
            id: 'legacy',
            label: 'Legacy',
            leadingIcon: (
              <Building2
                className="size-4 shrink-0 text-fg-muted"
                aria-hidden
              />
            ),
            config: entry.config.legacy,
            onChange: (value: FeatureFlagValueConfig<false>['current']) =>
              setAction('legacy', value)
          }
        ]
      : []),
    ...filteredWorkspaces.map(column => ({
      id: `workspace-${column.id}`,
      label: column.name,
      leadingIcon: (
        <EnterpriseWorkspaceIcon name={column.name} iconUrl="" size="sm" />
      ),
      config: entry.config.workspaces[column.id] ?? {
        supported: false as const
      },
      onChange: (value: FeatureFlagValueConfig<false>['current']) =>
        setAction({ workspaceId: column.id }, value)
    }))
  ];

  const serverListEmpty =
    hasServer && !includeLegacy && filteredWorkspaces.length === 0;
  const nothingMatches =
    Boolean(query) &&
    !includeLegacy &&
    filteredWorkspaces.length === 0 &&
    !includeFrontend;

  return (
    <ScrollShadowWrapper
      renderTopShadow={false}
      className="min-h-0 min-w-0 flex-1 overflow-y-auto with-styled-scrollbars"
    >
      <div
        className={cn(
          '-header sticky top-0 z-[1] space-y-1 bg-bg-default px-5 py-4',
          '[[data-has-top-shadow]_&]:shadow-sm'
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold">{entry.featureName}</h3>
          {entry.shortcut && (
            <SimpleTooltip title="Keyboard shortcut">
              <Kbd className="bg-bg-subtle px-1.5 py-0.5 text-xs text-fg-default">
                {entry.shortcut
                  .split('+')
                  .map(key => key.charAt(0).toUpperCase() + key.slice(1))
                  .join(' ')}
              </Kbd>
            </SimpleTooltip>
          )}
          {entry.isBypassed && (
            <SimpleTooltip title="This feature is turned off by code and will be removed soon.">
              <span className="inline-block rounded-full bg-bg-subtle px-2 py-0.5 text-sm text-fg-default">
                Bypassed
              </span>
            </SimpleTooltip>
          )}
        </div>
        {entry.description && (
          <p className="text-sm text-fg-muted">{entry.description}</p>
        )}
        <p className="font-mono text-xs text-fg-subtle">{entry.featureId}</p>
        <FeatureFlagDetailSearch
          value={detailSearch}
          onChange={onDetailSearchChange}
          placeholder="Search scopes…"
          aria-label="Search scopes in detail"
        />
      </div>
      <div className="flex flex-col gap-4 p-5">
        {hasServer ? (
          <section className="rounded-lg border border-border-attention bg-bg-attention/40 p-4">
            <div className="mb-3 space-y-1">
              <h4 className="font-semibold text-fg-attention mix-blend-multiply">
                Shared on the server
              </h4>
              <p className="text-sm text-fg-muted mix-blend-multiply">
                Changing these values writes to the backend and affects other
                people in this organization. Legacy is the organization-wide
                set; each workspace is independent.
              </p>
            </div>
            {serverListEmpty ? (
              <p className="rounded-md border border-dashed border-border-muted bg-bg-subtle/40 px-4 py-3 text-sm text-fg-muted">
                No scopes match.
              </p>
            ) : (
              <FeatureFlagToggleListBox
                ariaLabel="Server scopes"
                header={
                  bulkState.canBulk ? (
                    <FeatureFlagBulkBar
                      bulkState={bulkState}
                      statusLabel={featureFlagBulkStatus({
                        ...bulkState,
                        emptyLabel: 'Apply to all scopes',
                        unit: 'scope'
                      })}
                      groupAriaLabel="Apply to all scopes"
                      onSetAll={enabled =>
                        bulkSetServerScopes(
                          entry,
                          filteredWorkspaceIds,
                          enabled,
                          onAction,
                          { includeLegacy }
                        )
                      }
                    />
                  ) : undefined
                }
                items={serverItems}
              />
            )}
          </section>
        ) : (
          <p className="rounded-lg border border-dashed border-border-muted bg-bg-subtle/40 px-4 py-3 text-sm text-fg-muted">
            This flag is not stored on the server — it only exists as a local
            browser override.
          </p>
        )}

        {includeFrontend && (
          <section className="rounded-lg border border-border-success bg-bg-success/30 p-4">
            <div className="mb-3 space-y-1">
              <h4 className="font-semibold text-fg-success mix-blend-multiply">
                This browser only
              </h4>
              <p className="text-sm text-fg-muted mix-blend-multiply">
                Safe to change — stored in localStorage on this device. Does not
                affect others. When set, overrides the resolved value for every
                workspace in this browser.
              </p>
            </div>
            <div className="rounded-md border border-border-muted/60 bg-bg-default/60">
              <FrontendOverrideControl
                config={entry.config.frontend}
                onChange={value => setAction('frontend', value)}
                onClear={() =>
                  onAction({
                    type: 'clearFeatureFlag',
                    source: 'frontend',
                    featureId: entry.featureId
                  })
                }
              />
            </div>
          </section>
        )}

        {nothingMatches && !hasServer && (
          <p className="rounded-lg border border-dashed border-border-muted bg-bg-subtle/40 px-4 py-3 text-sm text-fg-muted">
            No scopes match.
          </p>
        )}
      </div>
    </ScrollShadowWrapper>
  );
}
